import { IonContent, IonIcon } from '@ionic/react';
import { globeOutline } from 'ionicons/icons';
import React from 'react';

import { UiTypography } from '@/components/ui/Typography';
import { S3_BUCKET_URL } from '@/constants';
import { useAccount } from '@/hooks/useAccount';
import { amplitudeModule } from '@/modules/amplitude';
import { apiV1GetMeAuthRequest } from '@/modules/api';
import { webCampaign } from '@/modules/webCampaign';
import { canLogEvents } from '@/utils/canLogEvents';
import { generateDownloadLink } from '@/utils/generateDownloadLink';

import {
  CheckIcon,
  ContactSection,
  ContentSection,
  PageStyles,
  Step,
  StepContent,
  StepHeader,
  StepNumber,
  StepsSection,
  StoreLinks,
  TitleSection,
  WebVersionLink,
} from './WithStepsWeb.styles';

const WithStepsWebVariant: React.FC = () => {
  const { user } = useAccount();
  const [authToken, setAuthToken] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchAuthToken = async () => {
      const auth = await apiV1GetMeAuthRequest();
      setAuthToken(auth.token);
    };
    fetchAuthToken();
  }, []);

  const downloadingAppLink = React.useMemo(() => {
    return generateDownloadLink(user?.id || '');
  }, [user?.id]);

  const fireAmplitude = (storeType: string) => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('download_click', {
        type: storeType,
      });
    }
  };

  return (
    <PageStyles>
      <IonContent>
        <ContentSection>
          <TitleSection>
            <CheckIcon>
              <img
                src="/assets/success-page/with-steps/check-icon.png"
                alt="Success"
              />
            </CheckIcon>
            <UiTypography variant="title-large" align="center">
              Congrats, your purchase
              <br />
              was successful!
            </UiTypography>
          </TitleSection>

          <StepsSection>
            <Step $completed>
              <StepNumber $completed>
                <img
                  src="/assets/success-page/with-steps/check-mark.png"
                  alt="Done"
                />
              </StepNumber>
              <StepContent $completed>
                <UiTypography variant="body-large">
                  Get your premium plan
                </UiTypography>
              </StepContent>
            </Step>

            <Step $active data-has-extra-content="true">
              <StepHeader>
                <StepNumber $active>
                  <UiTypography variant="title-medium">2</UiTypography>
                </StepNumber>
                <UiTypography variant="body-large">
                  Download the app
                </UiTypography>
              </StepHeader>
              <StoreLinks>
                <a
                  href={downloadingAppLink}
                  onClick={() => fireAmplitude('googleplay')}
                >
                  <img
                    src={`${S3_BUCKET_URL}/onboarding/googleplay.png`}
                    alt="Google Play"
                  />
                </a>
                {webCampaign.setup.hasWebAppLink && !!authToken && (
                  <WebVersionLink>
                    <a href={webCampaign.getAuthUrl(authToken)} target="_blank">
                      <IonIcon icon={globeOutline} />
                      <UiTypography
                        variant="label-large"
                        color="white"
                        component="span"
                      >
                        Web version
                      </UiTypography>
                    </a>
                  </WebVersionLink>
                )}
              </StoreLinks>
              <UiTypography variant="body-medium" className="email-hint">
                OR download the app via the link we sent to your email,{' '}
                {user?.email} (check Inbox and Spam folders).
              </UiTypography>
            </Step>

            <Step>
              <StepNumber>
                <UiTypography variant="title-medium">3</UiTypography>
              </StepNumber>
              <StepContent>
                <UiTypography variant="body-large">
                  Enjoy unlimited chats, exclusive content from your favorite
                  characters and more! ❤️‍🔥
                </UiTypography>
              </StepContent>
            </Step>
          </StepsSection>

          <ContactSection>
            <UiTypography variant="body-medium" align="center">
              If you have any questions - reach us at{' '}
              <a href={`mailto:${webCampaign.getLegalSupportEmail()}`}>
                <strong>{webCampaign.getLegalSupportEmail()}</strong>
              </a>
            </UiTypography>
          </ContactSection>
        </ContentSection>
      </IonContent>
    </PageStyles>
  );
};

export default WithStepsWebVariant;
